import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import FadeLoader from 'react-spinners/FadeLoader';

function SubscriptionOptions() {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [userId, setUserId] = useState(null);
    const [accessToken, setAccessToken] = useState(null);

    useEffect(() => {
        // Get user data from localStorage
        const storedUserId = localStorage.getItem('userId') || localStorage.getItem('user_id');
        const storedToken = localStorage.getItem('token') || localStorage.getItem('access_token');

        console.log('SubscriptionOptions - userId:', storedUserId);
        console.log('SubscriptionOptions - token:', storedToken ? 'EXISTS' : 'MISSING');

        if (!storedUserId || !storedToken) {
            toast.error('Please sign in first');
            navigate('/login');
            return;
        }

        setUserId(storedUserId);
        setAccessToken(storedToken);
    }, [navigate]);

    const handlePlanSelection = async (plan) => {
        setIsLoading(true);

        try {
            if (plan === 'trial') {
                // For 7-day trial: redirect to payment method collection (Stripe Checkout setup mode)
                console.log('Creating Stripe Checkout session for 7-day trial...');
                
                const checkoutRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/create_checkout_session_payment_method/`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify({
                        user_id: userId,
                        is_trial: true, // Flag to indicate this is for 7-day trial
                    }),
                });

                if (!checkoutRes.ok) {
                    throw new Error('Failed to create checkout session');
                }

                const checkoutData = await checkoutRes.json();
                console.log('Checkout session created:', checkoutData);

                // Redirect to Stripe Checkout
                window.location.href = checkoutData.checkout_url;

            } else if (plan === 'monthly' || plan === 'yearly') {
                // For paid plans: create Stripe Checkout session for subscription
                console.log(`Creating Stripe Checkout session for ${plan} plan...`);
                
                const checkoutRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/get_checkout_url/${userId}/`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify({
                        plan: plan,
                        success_url: `${window.location.origin}/subscription-success`,
                        cancel_url: `${window.location.origin}/subscription-options`,
                    }),
                });

                if (!checkoutRes.ok) {
                    throw new Error('Failed to create checkout session');
                }

                const checkoutData = await checkoutRes.json();
                console.log('Checkout session created:', checkoutData);

                // Redirect to Stripe Checkout
                window.location.href = checkoutData.url;
            }

        } catch (error) {
            console.error('Error selecting plan:', error);
            toast.error(error.message || 'Failed to process your selection');
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 py-12 px-4">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="text-center mb-12">
                    <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                        Choose Your Plan
                    </h1>
                    <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                        Start your journey with IzzyAI and unlock unlimited access to personalized speech therapy
                    </p>
                </div>

                {/* Subscription Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
                    {/* 7-Day Trial Card */}
                    <div className="bg-gradient-to-br from-purple-500 to-blue-600 rounded-3xl p-8 text-white relative overflow-hidden shadow-2xl transform hover:scale-105 transition-all duration-300">
                        <div className="absolute top-0 right-0 bg-yellow-400 text-gray-900 px-4 py-1 rounded-bl-2xl font-bold text-sm">
                            🎁 POPULAR
                        </div>
                        
                        <div className="mb-6">
                            <h2 className="text-3xl font-bold mb-2">7-Day Trial</h2>
                            <p className="text-purple-100 text-sm">Try before you commit</p>
                        </div>

                        <div className="mb-6">
                            <div className="flex items-baseline">
                                <span className="text-5xl font-bold">FREE</span>
                            </div>
                            <p className="text-purple-100 text-sm mt-2">for 7 days</p>
                        </div>

                        <ul className="space-y-3 mb-8 text-sm">
                            <li className="flex items-start">
                                <svg className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                Full access to all features
                            </li>
                            <li className="flex items-start">
                                <svg className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                AI-powered speech therapy
                            </li>
                            <li className="flex items-start">
                                <svg className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                Personalized progress tracking
                            </li>
                            <li className="flex items-start">
                                <svg className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                Cancel anytime
                            </li>
                            <li className="flex items-start text-yellow-300 font-semibold">
                                <svg className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                Auto-converts to $15.24/month
                            </li>
                        </ul>

                        <button
                            onClick={() => handlePlanSelection('trial')}
                            disabled={isLoading}
                            className="w-full bg-white text-purple-600 font-bold py-4 px-6 rounded-full hover:bg-gray-100 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isLoading ? 'Processing...' : 'Start Free Trial'}
                        </button>

                        <p className="text-xs text-center text-purple-100 mt-4">
                            Payment details required • No charge for 7 days
                        </p>
                    </div>

                    {/* Monthly Plan Card */}
                    <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-gray-200">
                        <div className="mb-6">
                            <h2 className="text-3xl font-bold text-gray-900 mb-2">Monthly</h2>
                            <p className="text-gray-600 text-sm">Flexible billing</p>
                        </div>

                        <div className="mb-6">
                            <div className="flex items-baseline">
                                <span className="text-5xl font-bold text-gray-900">$15.24</span>
                                <span className="text-gray-600 ml-2">/month</span>
                            </div>
                        </div>

                        <ul className="space-y-3 mb-8 text-sm text-gray-700">
                            <li className="flex items-start">
                                <svg className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                Everything in trial
                            </li>
                            <li className="flex items-start">
                                <svg className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                Billed monthly
                            </li>
                            <li className="flex items-start">
                                <svg className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                Cancel anytime
                            </li>
                            <li className="flex items-start">
                                <svg className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                Priority support
                            </li>
                        </ul>

                        <button
                            onClick={() => handlePlanSelection('monthly')}
                            disabled={isLoading}
                            className="w-full bg-gray-900 text-white font-bold py-4 px-6 rounded-full hover:bg-gray-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isLoading ? 'Processing...' : 'Subscribe Monthly'}
                        </button>
                    </div>

                    {/* Yearly Plan Card */}
                    <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-green-500 relative">
                        <div className="absolute top-0 right-0 bg-green-500 text-white px-4 py-1 rounded-bl-2xl font-bold text-sm">
                            💰 SAVE 17%
                        </div>

                        <div className="mb-6">
                            <h2 className="text-3xl font-bold text-gray-900 mb-2">Yearly</h2>
                            <p className="text-gray-600 text-sm">Best value</p>
                        </div>

                        <div className="mb-6">
                            <div className="flex items-baseline">
                                <span className="text-5xl font-bold text-gray-900">$152</span>
                                <span className="text-gray-600 ml-2">/year</span>
                            </div>
                            <p className="text-green-600 font-semibold text-sm mt-2">
                                Save $30.88 per year
                            </p>
                        </div>

                        <ul className="space-y-3 mb-8 text-sm text-gray-700">
                            <li className="flex items-start">
                                <svg className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                Everything in monthly
                            </li>
                            <li className="flex items-start">
                                <svg className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                Billed annually
                            </li>
                            <li className="flex items-start font-semibold text-green-600">
                                <svg className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                17% discount
                            </li>
                            <li className="flex items-start">
                                <svg className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                VIP support
                            </li>
                        </ul>

                        <button
                            onClick={() => handlePlanSelection('yearly')}
                            disabled={isLoading}
                            className="w-full bg-green-600 text-white font-bold py-4 px-6 rounded-full hover:bg-green-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isLoading ? 'Processing...' : 'Subscribe Yearly'}
                        </button>
                    </div>
                </div>

                {/* Features Section */}
                <div className="bg-white rounded-3xl p-8 shadow-lg max-w-4xl mx-auto">
                    <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                        What's Included in All Plans
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="flex items-start">
                            <div className="bg-purple-100 rounded-full p-2 mr-4">
                                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div>
                                <h4 className="font-semibold text-gray-900">Unlimited AI Therapy</h4>
                                <p className="text-gray-600 text-sm">24/7 access to personalized speech therapy</p>
                            </div>
                        </div>
                        <div className="flex items-start">
                            <div className="bg-blue-100 rounded-full p-2 mr-4">
                                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                                </svg>
                            </div>
                            <div>
                                <h4 className="font-semibold text-gray-900">Progress Tracking</h4>
                                <p className="text-gray-600 text-sm">Detailed insights and personalized reports</p>
                            </div>
                        </div>
                        <div className="flex items-start">
                            <div className="bg-green-100 rounded-full p-2 mr-4">
                                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div>
                                <h4 className="font-semibold text-gray-900">Interactive Games</h4>
                                <p className="text-gray-600 text-sm">Fun exercises to keep you engaged</p>
                            </div>
                        </div>
                        <div className="flex items-start">
                            <div className="bg-yellow-100 rounded-full p-2 mr-4">
                                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                                </svg>
                            </div>
                            <div>
                                <h4 className="font-semibold text-gray-900">Premium Features</h4>
                                <p className="text-gray-600 text-sm">Advanced AI exercises and priority support</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Money-Back Guarantee */}
                <div className="text-center mt-12">
                    <p className="text-gray-600 text-sm">
                        🔒 Secure payment processing • 30-day money-back guarantee • Cancel anytime
                    </p>
                </div>
            </div>

            {/* Loading Overlay */}
            {isLoading && (
                <div className="fixed top-0 left-0 w-screen h-screen flex justify-center items-center z-50 bg-black bg-opacity-50">
                    <div className="bg-white rounded-2xl p-8 shadow-2xl flex flex-col items-center">
                        <FadeLoader color="#8B5CF6" />
                        <p className="mt-6 text-gray-700 font-semibold">Processing your selection...</p>
                    </div>
                </div>
            )}
        </div>
    );
}

export default SubscriptionOptions;

